import React from "react";
import { SeeReferenceModal } from "features/AdminPanel/modules/SeeReferenceModal/SeeReferenceModal";
import { validURLV2 } from "utils/validations";
import {
  BrandLogo,
  ColorPalette,
  TabIcon,
  TabTitle,
  WebsiteAddress,
} from "../../modules/BrandAssets";
import { CustomFooterToggle } from "../../modules/CustomFooter";
import { CustomImageHandlerLayout } from "../CustomImageHandlerLayout/CustomImageHandlerLayout";
import { CustomToggleLayout } from "../CustomToggleLayout";
import { InputSectionHandler } from "../InputSectionHandler/InputSectionHalder";
import { ExitIntentSettingsButton } from "../modules/ExitIntentSettingsButton/ExitIntentSettingsButton";
import { ShowEditContactForm } from "../modules/ExitIntentSettingsButton/ShowEditContactForm";
import { SECTION_TYPES } from "./Customization.constants";
import { getCustomizationAssets } from "./Customizations.utils";
import {
  CUSTOMIZATION_SECTIONKEYS,
  SHOW_LISTING_RATINGS_COPY,
} from "./Customization.keys";
export {
  CUSTOM_IMAGE_HANDLER_TEXTS,
  SECTION_TYPES,
} from "../CustomImageHandlerLayout/CustomImageHandlerLayout.constants";

export const ReorderTabs = {
  BASIC: "Basic",
  OFFERING_TYPE: "By Offering Type",
  CUSTOM: "Custom",
};

// Definition lives in the dependency-free `Customization.keys` module (see the
// doc comment there) and is re-exported so existing importers keep working.
export { CUSTOMIZATION_SECTIONKEYS };

export const WEBPAGE_CUSTOMIZATION_CONFIG = [
  {
    key: CUSTOMIZATION_SECTIONKEYS.BRAND_ASSETS,
    title: "Brand Assets",
    description:
      "Customize how your brand appears across your website, offerings, payments, and landing pages.",
    icon: getCustomizationAssets("lead.svg"),
    subSectionConfig: [
      {
        title: "Website address",
        description:
          "The URL where your website is accessible. This can be changed anytime.",
        key: CUSTOMIZATION_SECTIONKEYS.WEBSITE_ADDRESS,
        classes: { subSectionInnerWrapper: "nextLineSubSectionWrapper" },
        customRightJSX: <WebsiteAddress />,
      },
      {
        title: "Brand logo",
        description:
          "Your primary visual identity displayed on your website and marketing materials.",
        key: CUSTOMIZATION_SECTIONKEYS.BRAND_LOGO,
        classes: { subSectionInnerWrapper: "nextLineSubSectionWrapper" },
        customRightJSX: <BrandLogo />,
      },
      {
        title: "Tab title",
        description:
          "This text is shown on the browser tab when users visit your site.",
        key: CUSTOMIZATION_SECTIONKEYS.TAB_TITLE,
        classes: { subSectionInnerWrapper: "nextLineSubSectionWrapper" },
        customRightJSX: <TabTitle />,
        seeReferenceConfig: {
          id: CUSTOMIZATION_SECTIONKEYS.TAB_TITLE,
          url: getCustomizationAssets("tab_title.png"),
          height: "tw-py-[14px]",
          title: "Tab title",
          description:
            "This text is shown on the browser tab when users visit your site.",
        },
      },
      {
        title: "Tab icon",
        description:
          "This is the small icon (favicon) displayed on the browser tab when users visit your site.",
        key: CUSTOMIZATION_SECTIONKEYS.TAB_ICON,
        classes: { subSectionInnerWrapper: "nextLineSubSectionWrapper" },
        customRightJSX: <TabIcon />,
      },
      {
        title: "Colour palette",
        description:
          "A set of default brand colors applied to your offering pages, payment pages, and customer experience.",
        key: CUSTOMIZATION_SECTIONKEYS.COLOR_PALETTE,
        classes: { subSectionInnerWrapper: "nextLineSubSectionWrapper" },
        customRightJSX: <ColorPalette />,
      },
    ],
  },
  {
    key: CUSTOMIZATION_SECTIONKEYS.CUSTOM_FOOTER,
    title: "Custom Footer",
    description:
      "Control the visibility of the footer displayed on your webpage.",
    icon: getCustomizationAssets("lead.svg"),
    subSectionConfig: [
      {
        title: "Hide footer from webpage",
        description: "Hiding this will remove the footer from your webpage.",
        key: CUSTOMIZATION_SECTIONKEYS.HIDE_FOOTER,
        classes: { subSectionInnerWrapper: "nextLineSubSectionWrapper" },
        customRightJSX: <CustomFooterToggle />,
        seeReferenceConfig: {
          id: CUSTOMIZATION_SECTIONKEYS.HIDE_FOOTER,
          url: getCustomizationAssets("footer.png"),
          height: "tw-py-[14px]",
          title: "Custom Footer",
          description:
            "This footer is displayed on your webpage. Toggle this on to hide it from your visitors.",
        },
      },
    ],
  },
  {
    key: CUSTOMIZATION_SECTIONKEYS.LEAD_CONTACT,
    title: "Lead Contact from Homepage",
    description:
      "Control the visibility of the 'Get in Touch' button to allow or prevent website viewers from contacting you.",
    icon: getCustomizationAssets("lead.svg"),
    subSectionConfig: [
      {
        title: "Hide 'Get in Touch' contact button",
        description:
          "Hiding this will prevent potential leads from contacting you via the homepage.",
        key: CUSTOMIZATION_SECTIONKEYS.HIDE_GET_IN_TOUCH,
        classes: { subSectionInnerWrapper: "nextLineSubSectionWrapper" },
        customRightJSX: (
          <CustomToggleLayout
            sectionKey={CUSTOMIZATION_SECTIONKEYS.HIDE_GET_IN_TOUCH}
          />
        ),
        seeReferenceConfig: {
          id: CUSTOMIZATION_SECTIONKEYS.HIDE_GET_IN_TOUCH,
          url: [
            { url: "assets/vectors/homepage/lead1.png", heading: "Website:" },
            { url: "assets/vectors/homepage/lead2.png" },
            { url: "assets/vectors/homepage/lead3.png" },
            {
              url: "assets/vectors/homepage/lead4.png",
              heading: "Offering pages:",
            },
            { url: "assets/vectors/homepage/lead5.png", heading: "The Form:" },
          ],
          height: "tw-py-[14px]",
          title: "Get In Touch",
          description:
            "This form opens from your website and offering pages. It allows your page visitors to contact you by providing some details.",
        },
      },
      {
        title: "Manage Lead Contact Form",
        description:
          "Leads use this form to contact you via your homepage or event page. Manage it at: ‘My Accounts’ > ‘Lead contact forms’",
        key: CUSTOMIZATION_SECTIONKEYS.MANAGE_LEAD_CONTACT,
        classes: { subSectionInnerWrapper: "nextLineSubSectionWrapper" },
        customRightJSX: <ShowEditContactForm />,
        seeReferenceConfig: {
          id: CUSTOMIZATION_SECTIONKEYS.HIDE_GET_IN_TOUCH,
          url: [
            "assets/vectors/homepage/lead1.png",
            "assets/vectors/homepage/lead2.png",
            "assets/vectors/homepage/lead3.png",
            "assets/vectors/homepage/lead4.png",
            "assets/vectors/homepage/lead5.png",
          ],
          height: "tw-py-[14px]",
          title: "Get In Touch",
          description:
            "This form opens from your website and offering pages. It allows your page visitors to contact you by providing some details.",
        },
      },
      {
        title: "Manage exit lead capture form",
        description:
          "This form auto-appears after 1 min of inactivity on your homepage and booking pages. Manage it at: ‘My Accounts’ > ‘Lead contact forms’",
        key: CUSTOMIZATION_SECTIONKEYS.MANAGE_EXIT_LEAD,
        classes: { subSectionInnerWrapper: "nextLineSubSectionWrapper" },
        customRightJSX: <ExitIntentSettingsButton />,
        seeReferenceConfig: {
          id: CUSTOMIZATION_SECTIONKEYS.HIDE_GET_IN_TOUCH,
          url: [
            "assets/vectors/homepage/lead1.png",
            "assets/vectors/homepage/lead2.png",
            "assets/vectors/homepage/lead3.png",
            "assets/vectors/homepage/lead4.png",
            "assets/vectors/homepage/lead5.png",
          ],
          height: "tw-py-[14px]",
          title: "Get In Touch",
          description:
            "This form opens from your website and offering pages. It allows your page visitors to contact you by providing some details.",
        },
      },
    ],
  },
  {
    key: CUSTOMIZATION_SECTIONKEYS.TRUST_MARKERS,
    icon: getCustomizationAssets("trustmarkers.svg"),
    title: "Trust Markers",
    description:
      "Trust markers build credibility and reassure customers, encouraging bookings.",
    subSectionConfig: [
      {
        title: "Hide trust markers",
        description:
          "Your page visitors will not be able to see any trust-building icons from your event page.",
        key: CUSTOMIZATION_SECTIONKEYS.HIDE_TRUST_MARKERS,
        classes: { subSectionInnerWrapper: "nextLineSubSectionWrapper" },
        customRightJSX: (
          <CustomToggleLayout
            sectionKey={CUSTOMIZATION_SECTIONKEYS.HIDE_TRUST_MARKERS}
          />
        ),
        seeReferenceConfig: {
          id: CUSTOMIZATION_SECTIONKEYS.TRUST_MARKERS,
          url: [
            "assets/vectors/homepage/trust1.png",
            "assets/vectors/homepage/trust2.png",
          ],
          height: "tw-py-[14px]",
          title: "Trust Markers",
          description:
            "Trust markers are present on your offering pages. They build credibility, reassure visitors about the site's legitimacy and encourage actions like purchases.",
        },
      },
      {
        hideSubsectionText: true,
        customRightJSX: (
          <>
            <CustomImageHandlerLayout
              sectionKey={CUSTOMIZATION_SECTIONKEYS.TRUST_MARKERS}
              sectionName={SECTION_TYPES.WEBPAGE}
            />
            <SeeReferenceModal />
          </>
        ),
        classes: { subSectionInnerWrapper: "nextLineSubSectionWrapper" },
      },
    ],
  },
  {
    key: CUSTOMIZATION_SECTIONKEYS.SHOW_LISTING_RATINGS,
    icon: getCustomizationAssets("trustmarkers.svg"),
    title: SHOW_LISTING_RATINGS_COPY.section_title,
    description: SHOW_LISTING_RATINGS_COPY.section_description,
    subSectionConfig: [
      {
        title: SHOW_LISTING_RATINGS_COPY.toggle_label,
        description: SHOW_LISTING_RATINGS_COPY.toggle_description,
        key: CUSTOMIZATION_SECTIONKEYS.SHOW_LISTING_RATINGS,
        classes: { subSectionInnerWrapper: "nextLineSubSectionWrapper" },
        customRightJSX: (
          <CustomToggleLayout
            sectionKey={CUSTOMIZATION_SECTIONKEYS.SHOW_LISTING_RATINGS}
          />
        ),
      },
    ],
  },
  {
    key: CUSTOMIZATION_SECTIONKEYS.BOOKING_TNC,
    title: "T&C before booking",
    description:
      "Add a T&C URL for customers to view and acknowledge before booking.",
    icon: getCustomizationAssets("tncIcon.svg"),
    subSectionConfig: [
      {
        title: "Show 'Terms & Conditions' before booking",
        description:
          "If you add a URL here, customers will have to acknowledge that they have read the T&C before booking.",
        key: CUSTOMIZATION_SECTIONKEYS.CUSTOM_TNC,
        classes: { subSectionInnerWrapper: "nextLineSubSectionWrapper" },
        customRightJSX: (
          <InputSectionHandler
            sectionKey={CUSTOMIZATION_SECTIONKEYS.CUSTOM_TNC}
            placeholder="Paste URL"
            validate={(value) => {
              if (!value) return null;
              if (!validURLV2(value)) {
                return "Please enter a valid URL";
              }
              return null;
            }}
          />
        ),
        seeReferenceConfig: {
          id: CUSTOMIZATION_SECTIONKEYS.CUSTOM_TNC,
          url: "assets/vectors/homepage/TNC.png",
          height: "tw-py-[14px]",
          title: "Terms and Conditions",
          description:
            "This URL will be displayed in the booking popup as a hyperlink for 'Terms and Conditions'. Visitors will need to acknowledge the same to be able to book.",
        },
      },
    ],
  },
  {
    key: CUSTOMIZATION_SECTIONKEYS.MANAGE_BOOKING,
    title: "Manage Bookings Page Details",
    description:
      "Customize the texts and visibility of the different fields that appear on your booking page(s)",
    icon: getCustomizationAssets("BookingIcon.svg"),
    subSectionConfig: [
      {
        title: "Edit 'At my personal address' text",
        description:
          "This header text appears on the event page when you select 'At my personal address' as the meeting location.",
        key: CUSTOMIZATION_SECTIONKEYS.PERSONAL_ADDRESS_TITLE,
        classes: { subSectionInnerWrapper: "nextLineSubSectionWrapper" },
        customRightJSX: (
          <InputSectionHandler
            sectionKey={CUSTOMIZATION_SECTIONKEYS.PERSONAL_ADDRESS_TITLE}
            placeholder="Enter Text"
            defaultValue={"At my personal address"}
          />
        ),
        seeReferenceConfig: {
          id: CUSTOMIZATION_SECTIONKEYS.PERSONAL_ADDRESS_TITLE,
          url: "assets/vectors/homepage/personal_add.png",
          height: "tw-py-[14px]",
          title: "At my personal address",
          description:
            "This text appears on the event page when ’At my personal address’ is selected as the meeting location.",
        },
      },
      {
        title: "Hide your personal address",
        description:
          "If you choose 'At my personal address' as the meeting location, toggle this on to hide your address from visitors.",
        key: CUSTOMIZATION_SECTIONKEYS.HIDE_CREATOR_ADDRESS,
        classes: { subSectionInnerWrapper: "nextLineSubSectionWrapper" },
        customRightJSX: (
          <CustomToggleLayout
            invertedMode={true}
            sectionKey={CUSTOMIZATION_SECTIONKEYS.HIDE_CREATOR_ADDRESS}
          />
        ),
        seeReferenceConfig: {
          id: CUSTOMIZATION_SECTIONKEYS.PERSONAL_ADDRESS_TITLE,
          url: "assets/vectors/homepage/personal_add.png",
          height: "tw-py-[14px]",
          title: "Hide your personal address",
          description:
            "This text appears on the event page when ’At my personal address’ is selected as the meeting location.",
        },
      },
      {
        title: "Edit 'Phone' section text",
        description:
          "Appears on the event page when 'Phone' is selected as the meeting location.",
        key: CUSTOMIZATION_SECTIONKEYS.PHONE_TITLE,
        classes: { subSectionInnerWrapper: "nextLineSubSectionWrapper" },
        customRightJSX: (
          <InputSectionHandler
            sectionKey={CUSTOMIZATION_SECTIONKEYS.PHONE_TITLE}
            placeholder="Enter Text"
            defaultValue={"Call on phone"}
          />
        ),
        seeReferenceConfig: {
          id: CUSTOMIZATION_SECTIONKEYS.CUSTOM_TNC,
          url: "assets/vectors/homepage/phone.png",
          height: "tw-h-[75px]",
          title: "Phone title",
          description:
            "This text appears on the event page when ‘Phone’ is selected as the meeting location.",
        },
      },
      {
        title: "Edit 'Phone' description text",
        description:
          "Appears below the 'Phone' header when 'Phone' is selected as the meeting location.",
        key: CUSTOMIZATION_SECTIONKEYS.PHONE_DECRIPTION_TITLE,
        classes: { subSectionInnerWrapper: "nextLineSubSectionWrapper" },
        customRightJSX: (
          <InputSectionHandler
            sectionKey={CUSTOMIZATION_SECTIONKEYS.PHONE_DECRIPTION_TITLE}
            placeholder="Enter Text"
          />
        ),
        seeReferenceConfig: {
          id: CUSTOMIZATION_SECTIONKEYS.CUSTOM_TNC,
          url: "assets/vectors/homepage/phone.png",
          height: "tw-h-[75px]",
          title: "Phone description",
          description:
            "This text appears on the event page as description, when ‘Phone’ is selected as the meeting location.",
        },
      },
      {
        hideSubsectionText: true,
        customRightJSX: () => (
          <>
            <CustomImageHandlerLayout
              sectionKey={CUSTOMIZATION_SECTIONKEYS.TRUST_MARKERS}
              sectionName={SECTION_TYPES.WEBPAGE}
            />
            <SeeReferenceModal />
          </>
        ),
        classes: { subSectionInnerWrapper: "nextLineSubSectionWrapper" },
      },
      {
        title: "Edit 'Share event' button text",
        description:
          "This button appears on the event page, below your offering reviews. It provides the visitor with an offering link to share.",
        key: CUSTOMIZATION_SECTIONKEYS.SHARE_CTA_TEXT,
        classes: { subSectionInnerWrapper: "nextLineSubSectionWrapper" },
        customRightJSX: (
          <InputSectionHandler
            sectionKey={CUSTOMIZATION_SECTIONKEYS.SHARE_CTA_TEXT}
            placeholder="Enter Text"
            defaultValue={"Share event"}
          />
        ),
        seeReferenceConfig: {
          id: CUSTOMIZATION_SECTIONKEYS.SHARE_CTA_TEXT,
          url: "assets/vectors/homepage/sharevent.png",
          height: "tw-py-[14px]",
          title: "Share event",
          description:
            "This button appears on the event page, below your offering reviews. It provides the visitor with an offering link to share.",
        },
      },
      {
        title: "Edit 'Grab your certificate' section header",
        description:
          "This header text appears on the event page, where your certificate is displayed.",
        key: CUSTOMIZATION_SECTIONKEYS.CERTIFICATE_TITLE,
        classes: { subSectionInnerWrapper: "nextLineSubSectionWrapper" },
        customRightJSX: (
          <InputSectionHandler
            sectionKey={CUSTOMIZATION_SECTIONKEYS.CERTIFICATE_TITLE}
            placeholder="Enter Text"
            defaultValue={"Grab your certificate"}
          />
        ),
        seeReferenceConfig: {
          id: CUSTOMIZATION_SECTIONKEYS.CERTIFICATE_TITLE,
          url: "assets/vectors/homepage/certificate.png",
          height: "tw-py-[14px]",
          title: "Certificate",
          description:
            "This text 'Get certified!' appears on the event page, where your certificate is displayed. This only appears if your offering is a Recorded Content. It helps showcase value for money and bump sales.",
        },
      },
      {
        title: "Edit 'Meet your creator' section header",
        description:
          "This header text appears on the event page, where your details are shown.",
        key: CUSTOMIZATION_SECTIONKEYS.MEET_CREATOR_HEADER,
        classes: { subSectionInnerWrapper: "nextLineSubSectionWrapper" },
        customRightJSX: (
          <InputSectionHandler
            sectionKey={CUSTOMIZATION_SECTIONKEYS.MEET_CREATOR_HEADER}
            placeholder="Enter Text"
            defaultValue={"Meet your creator"}
          />
        ),
        seeReferenceConfig: {
          id: CUSTOMIZATION_SECTIONKEYS.MEET_CREATOR_HEADER,
          url: "assets/vectors/homepage/meetcreator.png",
          height: "tw-py-[14px]",
          title: "Creator Intro",
          description:
            "This text 'Meet your creator' appears on your website and offerings pages. It helps make an impactful introduction to your visitors.",
        },
      },
      {
        title: "Edit 'Visit website' button text",
        description:
          "This button appears on the event page, below your details. It redirects users to your website.",
        key: CUSTOMIZATION_SECTIONKEYS.VISIT_WEBSITE_CTA,
        classes: { subSectionInnerWrapper: "nextLineSubSectionWrapper" },
        customRightJSX: (
          <InputSectionHandler
            sectionKey={CUSTOMIZATION_SECTIONKEYS.VISIT_WEBSITE_CTA}
            placeholder="Enter Text"
            defaultValue={"Visit website"}
          />
        ),
        seeReferenceConfig: {
          id: CUSTOMIZATION_SECTIONKEYS.VISIT_WEBSITE_CTA,
          url: "assets/vectors/homepage/contact_creator.png",
          height: "tw-py-[14px]",
          title: "Visit website",
          description:
            "This button appears on the event page, below your own details. It redirects users to your website.",
        },
      },
      {
        title: "Edit 'Contact creator' button text",
        description:
          "This button appears on the event page, below your details. It opens the contact form for the user.",
        key: CUSTOMIZATION_SECTIONKEYS.CREATOR_CONTACT_CTA_TEXT,
        classes: { subSectionInnerWrapper: "nextLineSubSectionWrapper" },
        customRightJSX: (
          <InputSectionHandler
            sectionKey={CUSTOMIZATION_SECTIONKEYS.CREATOR_CONTACT_CTA_TEXT}
            placeholder="Enter Text"
            defaultValue={"Contact creator"}
          />
        ),
        seeReferenceConfig: {
          id: CUSTOMIZATION_SECTIONKEYS.CREATOR_CONTACT_CTA_TEXT,
          url: "assets/vectors/homepage/contact_creator.png",
          height: "tw-py-[14px]",
          title: "Contact creator",
          description:
            "This button appears on the event page, below your details. It opens the contact form for the user.",
        },
      },
    ],
  },
];
